import React from 'react';
import { FormattedMessage } from 'react-intl';
import ApiService from '../_shared/api/apiService';
import Singleton from '../Singleton';
import { BidStatus } from '../_shared/types/auction';
import { TradeState } from '../_shared/types/trade';
import {
  IBidFormValidation,
  IBidProxyValidationErrors
} from '../_shared/types/bid';
import convertToCurrency from '../_shared/utils/convertToCurrency';
import getQuickBidIncrements from '../_shared/utils/getQuickBidIncrements';

export default class BidService extends Singleton<BidService> {
  /**
   * Public Methods
   */

  /**
   * Gets list of all bids for the trade, including past auctions
   */
  public getBidList = async (tradeId: number) => {
    const client = await ApiService();
    let bidListResponse;

    try {
      bidListResponse = await client.apis.bid.listBids({
        request: {
          request: {
            tradeId
          }
        }
      });
    } catch (e) {
      // handle error
    }

    return bidListResponse?.body;
  };

  /**
   * Places a bid on the auction
   */
  public placeBid = async (
    tradeId: number,
    auctionId: number,
    bidAmount: number,
    proxyAmount: number = null
  ) => {
    if (!tradeId) {
      return false;
    }

    let errorMessage = null;
    let errorCode = null;
    const client = await ApiService();

    const bidCreateResponse = await client.apis.bid
      .createBid({
        request: {
          request: {
            tradeId,
            auctionId,
            bidAmount,
            proxyAmount
          }
        }
      })
      .catch(e => {
        errorMessage = e.response?.body?.message;
        errorCode = e.response?.body?.code;
      });

    return {
      success: bidCreateResponse?.ok,
      errorMessage,
      errorCode,
      auctionSummary: bidCreateResponse?.body?.auctionSummary
    };
  };

  /**
   * Places a bid on OMP
   */
  public placeOmpBid = async (
    dealerId: number,
    tradeId: number,
    auctionId: number,
    bidAmount: number,
    proxyAmount: number,
    validateSameDealershipOrDGN: boolean
  ) => {
    if (!tradeId || !dealerId) {
      return false;
    }

    let errorMessage = null;
    let errorCode = null;
    const client = await ApiService();

    const bidResponse = await client.apis.default
      .addBid({
        request: {
          tradeId,
          dealerId,
          auctionId,
          bidAmount,
          proxyAmount,
          validateSameDealershipOrDGN
        }
      })
      .catch(e => {
        errorMessage = e.response?.body?.description;
        errorCode = e.response?.body?.errorCode;
      });

    if (!bidResponse) {
      errorMessage = <FormattedMessage id="vdp.error.placingBid" />;
    }

    return {
      auctionSummary: bidResponse?.body?.auctionSummary,
      errorMessage,
      errorCode,
      success: bidResponse?.ok
    };
  };

  /**
   * Upcoming proxy
   */
  public placeUpcomingProxy = async (
    tradeId: number,
    bidAmount: number,
    proxyAmount: number
  ) => {
    if (!tradeId || !bidAmount || !proxyAmount) {
      return false;
    }

    let errorMessage = null;
    const client = await ApiService();
    const proxyCreateResponse = await client.apis.trade
      .createUpcomingProxy({
        request: {
          request: {
            tradeId,
            upcomingProxy: { bidAmount, proxyAmount }
          }
        }
      })
      .catch(e => {
        errorMessage = e.response?.body?.message;
      });

    return { success: proxyCreateResponse?.ok, errorMessage };
  };

  /**
   * Upcoming proxy on OMP
   */
  public placeOmpUpcomingProxy = async (
    dealerId: number,
    tradeId: number,
    auctionId: number,
    bidAmount: number,
    proxyAmount: number
  ) => {
    if (!tradeId || !bidAmount || !proxyAmount) {
      return false;
    }

    let errorMessage = null;
    let errorCode = null;

    const client = await ApiService();
    const proxyCreateResponse = await client.apis.default
      .addProxydBid({
        request: {
          tradeId,
          dealerId,
          auctionId,
          bidAmount,
          proxyAmount
        }
      })
      .catch(e => {
        errorMessage = e.response?.body?.description;
        errorCode = e.response?.body?.errorCode;
      });

    return { success: proxyCreateResponse?.ok, errorMessage, errorCode };
  };

  /**
   * Validation
   */
  public validateBidAmount = ({
    bidAmount,
    currentBidAmount,
    isSettingProxy,
    bidStatus,
    minBidAmount,
    proxyAmount,
    auctionSummary,
    configurations,
    tradeState,
    quickBidAmount,
    mpBidError: bidError
  }): IBidProxyValidationErrors => {
    const validation: any = [];
    const mpBidError: any = bidError || [];

    const userBidAmount = quickBidAmount || bidAmount;

    if (userBidAmount) {
      /**
       * bidAmount must always be:
       * - More than the current bid on the auction
       *   - Or more/equals than the minimum bid
       * - In the right increment
       * - Less than the proxy amount
       *
       * Only if isSettingProxy, bidAmount must be:
       * - More than the current bid on the auction (different wording)
       *   - Or more/equals than the minimum bid (different wording)
       */
      if (currentBidAmount) {
        if (userBidAmount <= currentBidAmount) {
          if (
            isSettingProxy &&
            (bidStatus !== BidStatus.topBidder || bidStatus !== BidStatus.proxy)
          ) {
            validation.push(
              <FormattedMessage
                id="vdp.error.startingBidLow"
                values={{
                  minBidAmount: convertToCurrency(minBidAmount)
                }}
              />
            );
            mpBidError.push('bid_lower_than_current_bid_amount');
          } else {
            validation.push(<FormattedMessage id="vdp.error.bidTooLow" />);
            mpBidError.push('bid_low');
          }
        } else if (proxyAmount && userBidAmount >= proxyAmount) {
          validation.push(
            <FormattedMessage id="vdp.error.startingBidLargerThanProxy" />
          );
          mpBidError.push('bid_larger_than_proxy');
        } else if (
          userBidAmount %
            getQuickBidIncrements(userBidAmount, configurations) !==
          0
        ) {
          validation.push(
            <FormattedMessage
              id="vdp.error.wrongBidIncrement"
              values={{
                quickBidIncrement: convertToCurrency(
                  getQuickBidIncrements(userBidAmount, configurations)
                )
              }}
            />
          );
          mpBidError.push('wrong_bid_increment');
        }
      } else if (userBidAmount < auctionSummary?.minBidThreshold) {
        if (
          isSettingProxy &&
          (bidStatus !== BidStatus.topBidder || bidStatus !== BidStatus.proxy)
        ) {
          validation.push(
            <FormattedMessage
              id="vdp.error.startingBidLow"
              values={{
                minBidAmount: convertToCurrency(minBidAmount)
              }}
            />
          );
        } else {
          validation.push(
            <FormattedMessage
              id="vdp.error.startingBidLow"
              values={{
                minBidAmount: convertToCurrency(auctionSummary?.minBidThreshold)
              }}
            />
          );
        }
        mpBidError.push('bid_below_min_threshold');
      } else if (
        userBidAmount % getQuickBidIncrements(userBidAmount, configurations) !==
        0
      ) {
        validation.push(
          <FormattedMessage
            id="vdp.error.wrongBidIncrement"
            values={{
              quickBidIncrement: convertToCurrency(
                getQuickBidIncrements(userBidAmount, configurations)
              )
            }}
          />
        );
        mpBidError.push('wrong_bid_increment');
      } else if (proxyAmount && userBidAmount >= proxyAmount) {
        validation.push(
          <FormattedMessage id="vdp.error.startingBidLargerThanProxy" />
        );
        mpBidError.push('bid_larger_than_proxy');
      }
    } else if (tradeState !== TradeState.active || !proxyAmount) {
      /**
       * bidAmount may be empty iff:
       * - In Active, the proxy amount is set, Starting bid cannot be edited/set
       */
      if (isSettingProxy) {
        if (
          bidStatus !== BidStatus.topBidder &&
          bidStatus !== BidStatus.proxy
        ) {
          validation.push(<FormattedMessage id="vdp.error.noStartingBid" />);
          mpBidError.push('no_starting_bid');
        }
      } else {
        validation.push(<FormattedMessage id="vdp.error.noBid" />);
        mpBidError.push('no_bid');
      }
    }

    return { validation, mpBidError };
  };

  public validateProxyAmount = ({
    isSettingProxy,
    proxyAmount,
    currentProxyAmount,
    minProxyAmount,
    configurations
  }): IBidProxyValidationErrors => {
    const validation: any = [];
    const mpBidError: any = [];

    if (!isSettingProxy) {
      return { validation, mpBidError };
    }

    /**
     * Proxy amount must be:
     * - More than the current bid on the auction
     *   - Or more/equals than the minimum bid
     * - In the right increment
     */
    if (!proxyAmount) {
      if (currentProxyAmount) {
        /**
         * Editing existing proxy, starting bid is not present
         */
        validation.push(<FormattedMessage id="vdp.error.noProxy" />);
        mpBidError.push('no_proxy');
      } else {
        validation.push(
          <FormattedMessage id="vdp.error.noProxyNoStartingBid" />
        );
        mpBidError.push('no_starting_bid_on_proxy');
      }
    } else if (proxyAmount < minProxyAmount && !currentProxyAmount) {
      validation.push(
        <FormattedMessage
          id="vdp.error.proxyTooLow"
          values={{
            minProxyAmount: convertToCurrency(minProxyAmount)
          }}
        />
      );
      mpBidError.push('proxy_too_low');
    } else if (
      proxyAmount % getQuickBidIncrements(proxyAmount, configurations) !==
      0
    ) {
      validation.push(
        <FormattedMessage
          id="vdp.error.wrongProxyIncrement"
          values={{
            proxyIncrement: convertToCurrency(
              getQuickBidIncrements(proxyAmount, configurations)
            )
          }}
        />
      );
      mpBidError.push('wrong_proxy_increment');
    }

    return { validation, mpBidError };
  };

  public validateForm = ({
    bidAmount,
    currentBidAmount,
    isSettingProxy,
    bidStatus,
    minBidAmount,
    proxyAmount,
    auctionSummary,
    currentProxyAmount,
    minProxyAmount,
    configurations,
    tradeState,
    quickBidAmount
  }): IBidFormValidation => {
    const {
      validation: bidValidationErrors,
      mpBidError: bidMpErrors
    } = this.validateBidAmount({
      bidAmount,
      currentBidAmount,
      isSettingProxy,
      bidStatus,
      minBidAmount,
      proxyAmount,
      auctionSummary,
      configurations,
      tradeState,
      quickBidAmount
    });

    const {
      validation: proxyValidationErrors,
      mpBidError: proxyMpErrors
    } = this.validateProxyAmount({
      isSettingProxy,
      proxyAmount,
      currentProxyAmount,
      minProxyAmount,
      configurations
    });

    return {
      validationErrors: {
        bidValidationErrors,
        proxyValidationErrors
      },
      mpBidErrors: [...bidMpErrors, ...proxyMpErrors]
    };
  };
}
